import React from 'react';
import { 
  Activity, ShieldCheck, Cpu, Database, 
  Zap, CheckCircle2, Lock, ArrowRight, 
  Terminal, Globe, Microscope, FileSearch
} from 'lucide-react';
import EvalHeader from './EvalHeader';
import EvaluationLogicSection from './LogicSection';
import ReportShowcase from './ReportShowcase';
import Footer from './footer';
import HeroSection from './HeroSection';

const HomePage = () => {
  return (
    <div className="min-h-screen bg-[#0F172A] text-slate-100 font-sans selection:bg-indigo-500">
      <EvalHeader />

      <main className="max-w-[1400px] mx-auto p-4 md:p-12 space-y-24">
        
        {/* --- 1. THE ARCHITECTURAL NARRATIVE (THE "WHY") --- */}
        <section className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center pt-10">
          <div className="space-y-8">
            <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 text-[10px] font-black uppercase tracking-[0.2em]">
              <Terminal size={14} />
              Protocol: Automated Synthesis
            </div>
            <h1 className="text-6xl md:text-7xl font-black uppercase tracking-tighter leading-[0.9]">
              Beyond Data <br />
              <span className="text-indigo-500">Pure Evaluation.</span>
            </h1>
            <p className="text-lg text-slate-400 leading-relaxed font-medium">
              Traditional NDT reporting is a fractured process. Inspectors spend 70% of their time 
              manually transcribing coordinates from hardware to spreadsheets. EvalNDT bridges 
              this gap by acting as a **deterministic logic layer** between raw ultrasonic signal 
              acquisition and final certification.
            </p>
            <div className="flex gap-6">
              <div className="space-y-2">
                <h4 className="text-white font-black text-3xl italic">100%</h4>
                <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Data Integrity</p>
              </div>
              <div className="w-[1px] bg-slate-800" />
              <div className="space-y-2">
                <h4 className="text-indigo-500 font-black text-3xl italic">2.5x</h4>
                <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Project Turnover</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4">
            <div className="bg-slate-800/40 p-8 rounded-[2rem] border border-slate-700/50 hover:border-indigo-500/50 transition-colors group">
              <Zap className="text-indigo-500 mb-4 transition-transform group-hover:scale-110" />
              <h3 className="text-xl font-black uppercase mb-2">Eliminating Transcription Bias</h3>
              <p className="text-sm text-slate-400 leading-relaxed">
                Manual entry is prone to human parallax and fatigue. Our engine ingests CSV/TXT 
                files directly from OmniScan and X4 hardware, ensuring the coordinates in your 
                report are a 1:1 mathematical match to the scan.
              </p>
            </div>
            <div className="bg-slate-800/40 p-8 rounded-[2rem] border border-slate-700/50 hover:border-indigo-500/50 transition-colors group">
              <Microscope className="text-indigo-500 mb-4 transition-transform group-hover:scale-110" />
              <h3 className="text-xl font-black uppercase mb-2">Regulatory Calibration</h3>
              <p className="text-sm text-slate-400 leading-relaxed">
                The engine doesn't just "read" data; it evaluates it. By pre-loading ASME, API, or 
                ISO acceptance criteria, EvalNDT automatically categorizes indications as 
                Acceptable, Marginal, or Rejectable.
              </p>
            </div>
          </div>
        </section>

        {/* --- 2. THE THREE-LAYER ENGINE (THE "HOW") --- */}
        <section className="space-y-12">
          <div className="text-center space-y-4">
            <h2 className="text-[10px] font-black uppercase tracking-[0.5em] text-indigo-500">The Evaluation Pipeline</h2>
            <h3 className="text-4xl font-black uppercase tracking-tighter">How the Engine Processes Files</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: "Data Ingestion",
                icon: <Database />,
                desc: "The system identifies probe frequency, aperture, and wedge delay from your setup file. It creates a digital twin of the inspection environment."
              },
              {
                title: "Signal Parsing",
                icon: <Activity />,
                desc: "Raw A-Scan and S-Scan amplitudes are parsed. The logic engine calculates signal-to-noise ratios and isolates valid flaws from geometry echoes."
              },
              {
                title: "Synthesis",
                icon: <FileSearch />,
                desc: "Indication data is cross-referenced with the selected standard. A multi-page, draft PDF is generated, ready for Level III review."
              }
            ].map((step, i) => (
              <div key={i} className="relative p-10 bg-slate-900 rounded-[2.5rem] border border-slate-800 overflow-hidden group">
                <div className="text-indigo-500 mb-6 bg-indigo-500/10 w-12 h-12 flex items-center justify-center rounded-xl">
                  {step.icon}
                </div>
                <h4 className="text-2xl font-black uppercase mb-4 tracking-tight">{step.title}</h4>
                <p className="text-slate-400 text-sm leading-relaxed font-medium">{step.desc}</p>
                <div className="absolute -right-4 -bottom-4 text-white/5 font-black text-9xl group-hover:text-indigo-500/10 transition-colors">
                  {i + 1}
                </div>
              </div>
            ))}
          </div>
        </section>

   <EvaluationLogicSection />

<ReportShowcase />

      </main>
<Footer />
     
    </div>
  );
};

export default HomePage;